import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, defer, timer, throwError } from 'rxjs';
import { exhaustMap, map, catchError, expand, first } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { Schedule, StationData, TrackInSchedule } from '../DataTypes';
import { ShareDataService } from './share-data.service';

// Interfaces for simulation responses
interface SimulationResponse {
  simulationId: string;
  message: string;
  status: string;
}

interface SimulationStatus {
  status: 'processing' | 'completed' | 'failed' | 'not_found';
  message: string;
  results?: any;
  error?: string;
}

/** Delay before the second status poll. Kept small so fast (Rust kernel) runs return quickly. */
const INITIAL_POLLING_INTERVAL_MS = 150;
/** Upper bound for the poll delay once a simulation turns out to be long-running. */
const MAX_POLLING_INTERVAL_MS = 2000;
/** Growth factor applied to the poll delay after each 'processing' response. */
const POLLING_BACKOFF_FACTOR = 1.6;

@Injectable({
  providedIn: 'root'
})
export class SimulationService {
  private readonly http = inject(HttpClient);
  private readonly shareDataService = inject(ShareDataService);

  /**
   * Start a simulation and poll for results
   * @param schedule The schedule configuration for the simulation
   * @param simulationKernel The simulation kernel to use ('python' or 'rust')
   * @returns Observable that emits the final simulation results
   */
  simulate(schedule: Schedule, simulationKernel: string = 'python'): Observable<any> {
    const path = `${environment.apiURL}/simulations`;
    const payload = this.mapScheduleToSimulationData(schedule);
    const startTime = performance.now();

    return this.http
      .post<SimulationResponse>(path, { data: payload, simulationKernel: simulationKernel })
      .pipe(
        exhaustMap(response => this.pollSimulationStatus(response.simulationId)),
        map(results => ({
          ...results,
          total_time_ms: Math.round(performance.now() - startTime)
        })),
        catchError(error => {
          console.error('Simulation error:', error);
          return throwError(() => error);
        })
      );
  }

  simulateAndExport(schedule: Schedule, simulationKernel: string = 'python'): Observable<any> {
    const path = `${environment.apiURL}/simulations/exports`;
    const payload = this.mapScheduleToSimulationData(schedule);

    return this.http.post(path, { data: payload, simulationKernel: simulationKernel });
  }

  /**
   * Poll for simulation status until completion.
   *
   * Uses a fast first poll with exponential backoff instead of a fixed
   * interval, so short simulations (Rust kernel) are picked up almost
   * immediately while long ones do not hammer the API.
   *
   * @param simulationId The ID of the simulation to monitor
   * @returns Observable that emits when simulation is complete
   */
  private pollSimulationStatus(simulationId: string): Observable<any> {
    const statusPath = `${environment.apiURL}/simulations/${simulationId}`;

    return defer(() => {
      let pollDelayMs = 0;
      const requestStatus = (): Observable<SimulationStatus> =>
        this.http.get<SimulationStatus>(statusPath);

      return requestStatus().pipe(
        expand(() => {
          pollDelayMs =
            pollDelayMs === 0
              ? INITIAL_POLLING_INTERVAL_MS
              : Math.min(pollDelayMs * POLLING_BACKOFF_FACTOR, MAX_POLLING_INTERVAL_MS);
          return timer(pollDelayMs).pipe(exhaustMap(requestStatus));
        })
      );
    }).pipe(
      // Use first() with a predicate - this will automatically complete when predicate returns true
      first(
        response =>
          response.status === 'completed' ||
          response.status === 'failed' ||
          response.status === 'not_found'
      ),
      map(response => {
        if (response.status === 'failed') {
          throw new Error(response.error ?? response.message ?? 'Simulation failed');
        }
        if (response.status === 'not_found') {
          throw new Error('Simulation not found');
        }
        return response.results; // Return the actual simulation results
      }),
      catchError(error => {
        console.error('Error polling simulation status:', error);
        return throwError(() => error);
      })
    );
  }

  /* Maps a schedule's tracks to the structure the simulation engine expects. */
  mapScheduleTracksToEngineTracks(tracks: TrackInSchedule[]): any[] {
    return tracks.map(track => ({
      trackParameters: track.trackParameters, // Only gets added if there is manually changed data
      trackID: track.scheduleData.drivingData[0].trackId,
      stations: track.scheduleData.stoppingData.map(station => ({
        trackName: station.trackName,
        stationName: station.stationName,
        doNotStopAtStation: station.doNotStopAtStation,
        removeStationFromRoute: false // Set default value to false // TODO
      }))
    }));
  }

  mapScheduleToSimulationData(schedule: Schedule): any {
    const completeScheduleDataMerged = this.getCompleteScheduleDataFromScheduleTracks(
      schedule.tracks,
      true
    );

    const tracks = this.mapScheduleTracksToEngineTracks(schedule.tracks);

    const vehicleSimulationData = this.shareDataService.getVehicleSimulationData();
    const vehicleData = {
      vehicle_id: vehicleSimulationData!.vehicleId,
      vehicle_scenario: vehicleSimulationData!.scenario.name
    };
    const simulationData = {
      vehicle_data: vehicleData,
      tracks: tracks,
      timetable_data: {
        timetable_name: schedule.name,
        timetable_id: schedule.id ?? null, // Set default value if schedule.id is undefined
        data: {
          driving_data: completeScheduleDataMerged.drivingData.map((segment: any) => ({
            driving_time_in_seconds: segment.drivingTime,
            driving_actions: segment.drivingActions
          })),
          stopping_data: completeScheduleDataMerged.stoppingData.map((segment: any) => ({
            stopping_time_in_seconds: segment.stoppingTime,
            stopping_actions: segment.stoppingActions
          }))
        }
      },
      simulation_parameters: {
        SOE_start: vehicleSimulationData!.availableFuel / 100,
        H2_level_start: vehicleSimulationData!.availableFuel / 100,
        driving_type: vehicleSimulationData!.drivingStyle
      }
    };
    return simulationData;
  }

  /*
    Maps all tracks in schedule to one ScheduleData object

    mergeTracks: If true, if the first station of a track is the same as the last station of the previous track,
    they get merged to one
    */
  getCompleteScheduleDataFromScheduleTracks(
    tracksFromSchedule: TrackInSchedule[],
    mergeTracks: boolean
  ): any {
    const scheduleData: any = {
      drivingData: [],
      stoppingData: []
    };

    if (!mergeTracks) {
      tracksFromSchedule.forEach((track: TrackInSchedule) => {
        scheduleData.drivingData.push(...track.scheduleData.drivingData);
        scheduleData.stoppingData.push(...track.scheduleData.stoppingData);
      });
      return scheduleData;
    } else {
      let previousTrack: TrackInSchedule | null = null;
      tracksFromSchedule.forEach((track: TrackInSchedule) => {
        if (previousTrack !== null) {
          const lengthOfPreviousTrack = previousTrack.scheduleData.stoppingData.length;
          const lastStationOfPreviousTrack =
            previousTrack.scheduleData.stoppingData[lengthOfPreviousTrack - 1];
          const firstStationOfCurrentTrack = track.scheduleData.stoppingData[0];
          if (lastStationOfPreviousTrack.stationName === firstStationOfCurrentTrack.stationName) {
            // Merge both stations
            const mergedStation: StationData = {
              ...lastStationOfPreviousTrack,
              // Add stopping times
              stoppingTime:
                lastStationOfPreviousTrack.stoppingTime + firstStationOfCurrentTrack.stoppingTime,
              // Merge stopping actions
              stoppingActions: [
                ...lastStationOfPreviousTrack.stoppingActions,
                ...firstStationOfCurrentTrack.stoppingActions
              ]
            };
            scheduleData.stoppingData[scheduleData.stoppingData.length - 1] = mergedStation;
            if (track.scheduleData.stoppingData.length > 1) {
              scheduleData.stoppingData.push(...track.scheduleData.stoppingData.slice(1));
            }
          } else {
            scheduleData.stoppingData.push(...track.scheduleData.stoppingData);
          }
        } else {
          scheduleData.stoppingData.push(...track.scheduleData.stoppingData);
        }
        scheduleData.drivingData.push(...track.scheduleData.drivingData);
        previousTrack = track;
      });

      // Map driving actions and stopping actions (array of string) to objects suitable for backend
      scheduleData.drivingData = scheduleData.drivingData.map((drivingData: any) => ({
        ...drivingData,
        drivingActions: drivingData.drivingActions.map((drivingAction: any) => ({
          driving_action_id: drivingAction,
          user_input: 'null'
        }))
      }));
      scheduleData.stoppingData = scheduleData.stoppingData.map((stoppingData: any) => ({
        ...stoppingData,
        stoppingActions: stoppingData.stoppingActions.map((stoppingAction: any) => ({
          stop_action_id: stoppingAction,
          user_input: 'null'
        }))
      }));

      return scheduleData;
    }
  }
}
